import ColorPicker from "./ColorPicker"
import { motion } from 'motion/react';
import { transition, variants, typeContainerVariants, typeVariants, typeLabelVariants, disabledVariants } from './variants';
import styles from './Helper.module.css';

// All
export const ColorAndGradientToggles = ({ colorType, handleColorTypeChange, gradientType, handleGradientTypeChange }) => (
    <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 1rem'}}>
        <div className={styles.inputGroup}>
            <div className={styles.label}>Color Type</div>
            <div className={styles.typeWrap}>
                <motion.label
                    className={styles.type}
                    transition={transition}
                    variants={typeContainerVariants}
                    initial={false}
                    animate={colorType}
                >
                    <input type='checkbox' checked={colorType === 'gradient'} onChange={handleColorTypeChange} />
                    <motion.div
                        className={styles.toggle}
                        transition={transition}
                        variants={typeVariants}
                        initial={false}
                        animate={colorType}
                    >
                        <motion.i
                            className='far fa-check'
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={colorType === 'gradient' ? 'animate' : 'initial'}
                        />
                    </motion.div>
                </motion.label>
                <motion.span
                    className={styles.typeLabel}
                    transition={transition}
                    variants={typeLabelVariants}
                    initial={false}
                    animate={colorType}
                >Gradient</motion.span>
            </div>
        </div>
        <div className={styles.inputGroup}>
            <motion.div 
                className={styles.label}
                transition={transition}
                variants={disabledVariants}
                initial={false}
                animate={colorType === 'gradient' ? 'enabled' : 'disabled'}
            >
                Gradient Type
            </motion.div>
            <div className={styles.typeWrap}>
                <motion.label
                    className={styles.type}
                    transition={transition}
                    variants={typeContainerVariants}
                    initial={false}
                    animate={gradientType}
                >
                    <input type='checkbox' checked={gradientType === 'radial'} onChange={handleGradientTypeChange} disabled={colorType === 'single'} />
                    <motion.div
                        className={styles.toggle}
                        transition={transition}
                        variants={typeVariants}
                        initial={false}
                        animate={gradientType}
                    >
                        <motion.i
                            className='far fa-check'
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={gradientType === 'radial' ? 'animate' : 'initial'}
                        />
                    </motion.div>
                </motion.label>
                <motion.span
                    className={styles.typeLabel}
                    transition={transition}
                    variants={typeLabelVariants}
                    initial={false}
                    animate={gradientType}
                >Radial</motion.span>
            </div>
        </div>
    </div>
);

const ColorInput = ({ color, onChange, name, display, setDisplay }) => (
    <>
    <div className={styles.swatch} onClick={() => setDisplay(name)}>
        <div className={styles.color} style={{background: color}} />
    </div>
    {display === name && (
        <div className={styles.popover}>
            <div className={styles.cover} onClick={() => setDisplay(null)} />
            <ColorPicker 
                name={name} 
                color={color} 
                onChange={onChange} 
            />
        </div>
    )}
    </>
);

export const ColorPickers = ({ colorType, firstColor, secondColor, handleColorChange, displayColorPicker, setDisplayColorPicker }) => (
    <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 .5rem'}}>
        <ColorInput
            name='single-color'
            color={firstColor}
            onChange={(color) => handleColorChange(color, 'single-color')}
            display={displayColorPicker}
            setDisplay={setDisplayColorPicker}
        />
        <div className={colorType === 'single' ? styles.gradientDisabled : styles.gradient}>
            <ColorInput
                name='gradient-color'
                color={secondColor}
                onChange={(color) => handleColorChange(color, 'gradient-color')}
                display={displayColorPicker}
                setDisplay={setDisplayColorPicker}
            />
        </div>
    </div>
);

export const Rotation = ({ show, rotation, handleRotationChange }) => (
    <motion.div 
        className={styles.inputGroup}
        transition={transition}
        variants={disabledVariants}
        initial={false}
        animate={show ? 'enabled' : 'disabled'}
    >
        <label htmlFor='rotation' className={styles.label}>Rotation</label>
        <input id='rotation' type='number' min='0' max='6' className={styles.rotation} value={rotation} onChange={handleRotationChange} disabled={!show} />
    </motion.div>
);

// Background
export const Transparency = ({ transparent, handleTransparentChange }) => (
    <div className={styles.inputGroup} style={{marginBottom: '.75rem'}}>
        <div className={styles.label}>Transparent</div>
        <div className={styles.typeWrap}>
            <motion.label
                className={styles.type}
                transition={transition}
                variants={typeContainerVariants}
                initial={false}
                animate={transparent ? 'on' : 'off'}
            >
                <input type='checkbox' checked={transparent} onChange={(e) => handleTransparentChange(e.target.checked)} />
                <motion.div
                    className={styles.toggle}
                    transition={transition}
                    variants={typeVariants}
                    initial={false}
                    animate={transparent ? 'on' : 'off'}
                >
                    <motion.i
                        className='far fa-check'
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={transparent ? 'animate' : 'initial'}
                    />
                </motion.div>
            </motion.label>
            {/* <motion.span
                className={styles.typeLabel}
                transition={transition}
                variants={typeLabelVariants}
                initial={false}
                animate={transparent ? 'on' : 'off'}
            >On</motion.span> */}
        </div>
    </div>
);

// Dots
export const dotsTypes = [
    { value: 'square', name: 'Square', imgSrc: '/images/square-dots.png' },
    { value: 'dots', name: 'Dots', imgSrc: '/images/dots-dots.png' },
    { value: 'random-dots', name: 'Random Dots', imgSrc: '/images/random-dots.png' },
    { value: 'rounded', name: 'Rounded', imgSrc: '/images/rounded-dots.png' },
    { value: 'extra-rounded', name: 'Extra Rounded', imgSrc: '/images/extra-rounded-dots.png' },
    { value: 'classy', name: 'Classy', imgSrc: '/images/classy-dots.png' },
    { value: 'classy-rounded', name: 'Classy Rounded', imgSrc: '/images/classy-rounded-dots.png' },
    { value: 'vertical-lines', name: 'Vertical Lines', imgSrc: '/images/vertical-dots.png' },
    { value: 'horizontal-lines', name: 'Horizontal Lines', imgSrc: '/images/horizontal-dots.png' }
];

// Corner Frames
export const cornersFrameTypes = [
    { value: 'square', name: 'Square', imgSrc: '/images/square-frame.png' },
    { value: 'dot', name: 'Dot', imgSrc: '/images/circle-frame.png' },
    { value: 'extra-rounded', name: 'Extra Rounded', imgSrc: '/images/extra-rounded-frame.png' },
];

// Corner Eyes
export const cornersEyeTypes = [
    { value: 'square', name: 'Square', imgSrc: '/images/square-eye.png' },
    { value: 'dot', name: 'Dot', imgSrc: '/images/circle-eye.png' },
    { value: 'heart', name: 'Heart', imgSrc: '/images/heart-eye.png' },
]